import React, { useState } from 'react';
import type { FC } from 'react';

import { DateTime } from 'luxon';

import { Box } from '@clutter/clean';

import { FormGroup, Table, Panel, NavLinkButton } from '@shared/components/bootstrap';
import { Pluralize } from '@shared/components/helpers';

import { dispatcherURL, ordersURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useCalendarQuery, useRegionsQuery } from '@admin/schema';

const RegionFormGroup: FC<{
  regionID: string | undefined;
  onChange: (regionID: string | undefined) => void;
}> = ({ regionID, onChange }) => {
  const { data, loading } = useRegionsQuery({ client });
  const regions = data?.regions;

  return (
    <FormGroup>
      <select
        disabled={loading}
        className="form-control"
        value={regionID ?? ''}
        onChange={(event) => onChange(event.target.value || undefined)}
      >
        <option value=""> - Region - </option>
        {regions?.map((region) => (
          <option key={region.id} value={region.id}>
            {region.name}
          </option>
        ))}
      </select>
    </FormGroup>
  );
};

export const Calendar: FC = () => {
  useTitle('Calendar');
  const [regionID, setRegionID] = useState<string | undefined>(undefined);

  const { data } = useCalendarQuery({ client });
  const calendar = data?.calendar;

  const groups = regionID
    ? calendar?.groups?.filter((group) => group.sections.some((section) => section.region.id === regionID))
    : calendar?.groups;

  return (
    <>
      <Panel>
        <Panel.Body>
          <RegionFormGroup regionID={regionID} onChange={setRegionID} />
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Body>
          <Table striped>
            {groups?.map((group, index) => (
              <React.Fragment key={index}>
                <thead>
                  <tr>
                    <th className="col-fill col-nowrap text-center" colSpan={3}>
                      {DateTime.fromISO(group.date).toLocaleString({
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {group.sections.map((section, index) => (
                    <React.Fragment key={index}>
                      {(!regionID || section.region.id === regionID) && (
                        <tr>
                          <td className="col-zero col-nowrap text-left">{section.region.name}</td>
                          <td className="col-fill col-nowrap text-left">
                            <strong>
                              <Pluralize count={section.dispatched} singular="order" plural="orders" />
                            </strong>
                            <span> of </span>
                            <strong>
                              <Pluralize count={section.total} singular="order" plural="orders" />
                            </strong>
                            <span> dispatched</span>
                          </td>
                          <td className="col-zero col-nowrap text-right">
                            <Box.Flex gap="4px">
                              <NavLinkButton
                                kind="primary"
                                to={dispatcherURL({
                                  date: group.date,
                                  regionID: section.region.parentID ?? section.region.id,
                                })}
                              >
                                Dispatch
                              </NavLinkButton>
                              <NavLinkButton
                                kind="primary"
                                to={ordersURL({ date: group.date, regionID: section.region.id })}
                              >
                                Orders
                              </NavLinkButton>
                            </Box.Flex>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </React.Fragment>
            ))}
          </Table>
        </Panel.Body>
      </Panel>
    </>
  );
};
